import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { GxInputDirective } from '../../../directives/gxinput.directive';
import { HighlightKeywordPipe } from '../../../pipes/highlight-keyword.pipe';
import { LetDirective } from '@ngrx/component';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { combineLatestWith, Observable, Subject } from 'rxjs';
import { coerceObservable } from '@angular/cdk/coercion/private';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { cleanStringForComparison } from '../../../core/utilities';
import { DropdownOption } from '../../../models/dropdown-option';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatTooltip } from '@angular/material/tooltip';
import { Dictionary } from '@ngrx/entity';
import { toObservable } from '@angular/core/rxjs-interop';
import { FlagEmojiPipe } from '../../../pipes/flag-emoji.pipe';

export enum DropdownSelectionChangeSource {
  USER = 'user',
  API = 'api'
}

export interface DropdownSelectionChange {
  source: DropdownSelectionChangeSource,
  value: string[]
}

@Component({
  selector: 'app-multi-select-with-search',
  imports: [
    GxInputDirective,
    HighlightKeywordPipe,
    LetDirective,
    MatCheckbox,
    MatDivider,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatPrefix,
    MatSuffix,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgClass,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    MatTooltip,
    FlagEmojiPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './multi-select-with-search.component.html',
  standalone: true,
  styleUrls: ['./multi-select-with-search.component.scss'],
})
export class MultiSelectWithSearchComponent implements OnInit, OnDestroy {
  @ViewChild('viewport') viewport: CdkVirtualScrollViewport;
  @ViewChild('header') header: ElementRef;

  @Input() searchFieldHidden: boolean = false;
  // will render a country flag emoji before the option name in the dropdown list
  // ! requires the option value to be a valid country code !
  @Input() showCountryFlag: boolean = false;
  @Input() itemSize: number = 40;
  // if there's enough space (not limited by maxItemContainerHeightPx), render this many items
  @Input() maxVisibleItems: number = 4;
  // if nested in grid filter, might want to limit max scrollable dropdown height so that
  // in case of overflow, another scrollbar isn't rendered on the wrapper
  @Input() minWidthPx: number = 0;
  protected maxItemContainerHeightPx: number = null;

  @Input() set maxHeightPx(maxHeight: number) {
    this.maxItemContainerHeightPx = maxHeight ?
        Math.round(maxHeight - this.header?.nativeElement?.clientHeight) :
        null;
  };

  @Input() set dropdownOptions(value: DropdownOption[] | Observable<DropdownOption[]>) {
    this.dropdownOptions$ = coerceObservable(value);
  }

  @Output() selectionChange = new EventEmitter<DropdownSelectionChange>();
  @Output() masterToggleClicked = new EventEmitter<void>();
  @Output() searchUsed = new EventEmitter<void>();

  protected dropdownOptions$: Observable<DropdownOption[]>;
  protected filteredDropdownOptions$: Observable<DropdownOption[]>;
  protected viewModel$: Observable<{
    dropdownOptions: DropdownOption[],
    filteredOptions: DropdownOption[]
  }>;

  protected searchFC: FormControl<string> = new FormControl('', { nonNullable: true });

  private selectionSignal: WritableSignal<{
    source: DropdownSelectionChangeSource,
    value: Dictionary<boolean>
  }> =
      signal({ source: DropdownSelectionChangeSource.API, value: {} });
  // computed from selectionSignal
  private selectionChangeComputedSignal = computed(() => {
    const currentSelectionArray = Object.keys(this.selectionSignal().value);
    const selectionChange: DropdownSelectionChange = {
      source: this.selectionSignal().source,
      value: currentSelectionArray,
    };
    return selectionChange;
  });
  private selectionCountComputedSignal = computed(() => {
    return this.selectionChangeComputedSignal().value.length;
  });

  private componentDestroyed = new Subject<void>();

  constructor(private cd: ChangeDetectorRef) {
    // Send upstream to update filter on selection change (Signal -> Observable)
    // toObservable must be used within an injection context
    toObservable(this.selectionChangeComputedSignal).pipe(
        takeUntil(this.componentDestroyed),
    ).subscribe((selectionChange) => {
      this.selectionChange.emit(selectionChange);
    });
  }

  ngOnInit() {
    this.setupDataFlow();

    // emit functionality usage
    this.searchFC.valueChanges.pipe(
        debounceTime(1000),
        takeUntil(this.componentDestroyed),
    ).subscribe(() => this.searchUsed.emit());
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  private setupDataFlow() {
    // 1. Setup filteredOptions$ and viewModel$
    this.filteredDropdownOptions$ = this.dropdownOptions$.pipe(
        combineLatestWith(this.searchFC.valueChanges.pipe(startWith(this.searchFC.value))),
        map(([options, search]) =>
            options.filter((option) => this.isOptionShown(option, search)),
        ),
        takeUntil(this.componentDestroyed),
    );

    this.viewModel$ = this.dropdownOptions$.pipe(
        combineLatestWith(this.filteredDropdownOptions$),
        map(([dropdownOptions, filteredOptions]) => ({ dropdownOptions, filteredOptions })),
        takeUntil(this.componentDestroyed),
    );

    // 2. Handle incoming dropdownOptions to set the initial selection structure
    this.dropdownOptions$.pipe(
        takeUntil(this.componentDestroyed),
    ).subscribe((options) => {
      const currentSelection = this.selectionSignal().value;
      const newSelectionValue: Dictionary<boolean> = {};

      // Build the new structure, preserving selection status if the option still exists
      options.forEach(o => {
        if (currentSelection[o.value]) {
          newSelectionValue[o.value] = true;
        }
      });

      this.selectionSignal.set({
        source: DropdownSelectionChangeSource.API,
        value: newSelectionValue,
      });
    });
  };

  /**
   * Determine whether dropdown option should be shown.
   * @param dropdownOption
   * @param searchVal
   * @protected
   */
  private isOptionShown(dropdownOption: DropdownOption, searchVal: string): boolean {
    if (!searchVal.length) {
      return true;
    }

    const cleanedOptionString = cleanStringForComparison(dropdownOption.name);
    const cleanedSearchString = cleanStringForComparison(searchVal);
    return cleanedOptionString.includes(cleanedSearchString);
  }

  public isChecked(key: string) {
    return this.selectionSignal().value[key];
  }

  protected onCheckboxChange(key: string, event: MatCheckboxChange) {
    this.selectionSignal.update(current => {
      const newState = { ...current.value };
      if (event.checked) {
        newState[key] = true;
      } else {
        // Cleaner state: delete the property instead of setting to false
        delete newState[key];
      }
      return { source: DropdownSelectionChangeSource.USER, value: newState };
    });
  }

  /**
   * Get the number of selected dropdown options
   * @protected
   */
  public getSelectionCount(): number {
    return this.selectionCountComputedSignal();
  }

  /**
   * Determine whether master toggle's state is 'checked'
   * @param filteredOptions
   * @protected
   */
  protected isToggleAllChecked(filteredOptions: DropdownOption[]) {
    const selection = this.selectionSignal().value;
    return filteredOptions.every(o => selection[o.value]);
  }

  /**
   * Determine whether master toggle's state is 'indeterminate'
   * @param filteredOptions
   * @protected
   */
  protected isToggleAllIndeterminate(filteredOptions: DropdownOption[]) {
    const selection = this.selectionSignal().value;
    let count = 0;

    for (const option of filteredOptions) {
      if (selection[option.value]) {
        count++;
      }
    }

    return filteredOptions?.length > count && count > 0;
  }

  /**
   * Toggle master toggle
   * @param activate
   * @param filteredOptions
   * @protected
   */
  protected toggleSelectAll(activate: boolean, filteredOptions: DropdownOption[]) {
    this.selectionSignal.update(current => {
      const newState = { ...current.value };

      filteredOptions.forEach((fo) => {
        if (activate) {
          newState[fo.value] = true;
        } else {
          delete newState[fo.value]; // Deselect by removing key
        }
      });

      this.masterToggleClicked.emit();
      return { source: DropdownSelectionChangeSource.USER, value: newState };
    });


  }

  protected trackByFn(index: number, option: DropdownOption) {
    return option.value;
  }

  /**
   * Compute dropdown height. Setting height: 100% on the container via CSS does not work when
   * placed inside an expansion panel.
   * @param filteredOptions
   * @protected
   */
  protected getDropdownHeight(filteredOptions: DropdownOption[]): number {
    const dropdownHeight = filteredOptions?.length <= this.maxVisibleItems ?
        (filteredOptions.length * this.itemSize) :
        (this.maxVisibleItems * this.itemSize);

    if (this.maxItemContainerHeightPx) {
      return this.maxItemContainerHeightPx < dropdownHeight ? this.maxItemContainerHeightPx : dropdownHeight;
    } else {
      return dropdownHeight;
    }
  }

  /**
   * Set current selection. Use for preselecting programmatically.
   * @param selection - The array of keys to set as selected.
   * @param source
   */
  public setSelection(selection: string[] | null, source = DropdownSelectionChangeSource.API) {
    this.selectionSignal.update(() => {
      const newSelection: Dictionary<boolean> = {};

      if (selection?.length) {
        selection.forEach(key => newSelection[key] = true);
      }

      return { source, value: newSelection };
    });
  }

  /**
   * Get current selection
   */
  public getSelection(): string[] {
    return this.selectionChangeComputedSignal().value;
  }

  /**
   * Reset current selection
   */
  public resetSelection(source = DropdownSelectionChangeSource.API): void {
    this.setSelection(null, source);
  }

  /**
   * Reset search field
   */
  public resetSearch() {
    this.searchFC.reset();
  }

  /**
   * Update the viewport dimensions and re-render. Might solve issue with 'invisible' checkbox options.
   */
  public checkViewportSize() {
    this.cd.markForCheck();
    this.viewport?.checkViewportSize();
  }

}
